/**
 * Vertical timeline for chat messages — shows a sequence of events with
 * status-colored dots, optional dates, and descriptions. Designed for
 * chronological narratives (campaign milestones, article publication history,
 * crisis event sequences, etc.).
 */
import { cn } from "@/lib/utils";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type TimelineItemStatus = "completed" | "current" | "upcoming";

export interface TimelineItem {
  title: string;
  description?: string | null;
  date?: string | null;
  status?: TimelineItemStatus | null;
}

export interface TimelineProps {
  items?: TimelineItem[] | null;
  /** Optional heading rendered above the timeline for context. */
  heading?: string | null;
  className?: string;
}

// ─── Status styling ────────────────────────────────────────────────────────────

/** Dot fill color keyed by status. `completed` uses primary, `current` uses
 *  chart-3 (a warm accent), `upcoming` stays muted so completed items pop. */
const STATUS_DOT_CLASS: Record<TimelineItemStatus, string> = {
  completed: "bg-primary",
  current: "bg-chart-3",
  upcoming: "bg-muted-foreground/30",
};

/** Resolves the dot class for a given status, defaulting to `completed`. */
const dotClassForStatus = (
  status: TimelineItemStatus | null | undefined,
): string => STATUS_DOT_CLASS[status ?? "completed"];

// ─── Component ─────────────────────────────────────────────────────────────────

const Timeline = ({ items, heading = null, className }: TimelineProps) => {
  const rows = Array.isArray(items) ? items : [];

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {heading != null && heading !== "" && (
        <span className="text-sm font-semibold text-foreground">{heading}</span>
      )}
      <div className="relative pl-6">
        {/* Vertical connector line — spans from the first dot to the last. */}
        <div className="absolute left-1.25 top-1.5 bottom-1.5 w-px bg-border" />

        <ol className="flex flex-col gap-4 list-none m-0 p-0">
          {rows.map((item, i) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: order is fixed per spec render
              key={i}
              className="relative min-w-0"
            >
              {/* Status dot */}
              <div
                className={cn(
                  "absolute -left-6 top-1.25 size-2.75 rounded-full ring-2 ring-background",
                  dotClassForStatus(item.status),
                )}
              />

              {/* Content */}
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground leading-snug">
                    {item.title}
                  </span>
                  {item.date != null && item.date !== "" && (
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm leading-none">
                      {item.date}
                    </span>
                  )}
                </div>

                {item.description != null && item.description !== "" && (
                  <p className="text-xs text-muted-foreground leading-relaxed m-0">
                    {item.description}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default Timeline;
